<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { authAPI } from '../service/api.js';

const props = defineProps({
  visible: { type: Boolean, required: true },
});
const emit = defineEmits(['update:visible']);

const toast = useToast();

const currentPassword = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const submitting = ref(false);

// Mirrors api/domain/user.go's MinPasswordLength/MaxPasswordLength (the
// max mirrors bcrypt's hard 72-byte limit) so the common case never
// round-trips to the server just to be rejected.
const MIN_LENGTH = 8;
const MAX_LENGTH = 72;

const reset = () => {
  currentPassword.value = '';
  newPassword.value = '';
  confirmNewPassword.value = '';
  submitting.value = false;
};

// Wipe state every time the dialog reopens so a previous attempt's input
// can't linger on a re-open.
watch(() => props.visible, (now) => {
  if (now) reset();
});

// Single source of truth for validity, used both to gate the submit button
// and to pick the toast message on submit — keeps the two from drifting out
// of sync with each other.
const validationError = () => {
  if (!currentPassword.value.trim()) return 'Current password is required.';
  if (newPassword.value.length < MIN_LENGTH) return `New password must be at least ${MIN_LENGTH} characters.`;
  if (newPassword.value.length > MAX_LENGTH) return `New password must be at most ${MAX_LENGTH} characters.`;
  if (newPassword.value !== confirmNewPassword.value) return 'New password and confirmation must be the same.';
  return null;
};

const isValid = () => validationError() === null;

const submit = async () => {
  const error = validationError();
  if (error) {
    toast.add({ severity: 'error', summary: 'Cannot change password', detail: error, life: 4000 });
    return;
  }

  submitting.value = true;
  try {
    await authAPI.changePassword({
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
    });
    toast.add({ severity: 'success', summary: 'Password updated', life: 2500 });
    close();
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Failed to change password',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    submitting.value = false;
  }
};

const close = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    :style="{ width: '28rem' }"
    header="Change Password"
  >
    <div class="form-body">
      <div class="form-group">
        <label for="current-password">Current Password <span class="required">*</span></label>
        <InputText
          id="current-password"
          v-model="currentPassword"
          type="password"
          autofocus
        />
      </div>
      <div class="form-group">
        <label for="new-password">New Password <span class="required">*</span></label>
        <InputText
          id="new-password"
          v-model="newPassword"
          type="password"
        />
        <small class="help">{{ MIN_LENGTH }}–{{ MAX_LENGTH }} characters.</small>
      </div>
      <div class="form-group">
        <label for="confirm-new-password">Confirm New Password <span class="required">*</span></label>
        <InputText
          id="confirm-new-password"
          v-model="confirmNewPassword"
          type="password"
        />
      </div>

      <div class="dialog-actions">
        <Button label="Cancel" severity="secondary" text @click="close" />
        <Button
          label="Change Password"
          icon="pi pi-key"
          :loading="submitting"
          :disabled="!isValid()"
          @click="submit"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.form-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}
.form-group label {
  font-weight: 500;
  color: #444;
  font-size: 0.875rem;
}
.required {
  color: #b91c1c;
}
.help {
  color: #777;
  font-size: 0.75rem;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style>
